import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { logoutUser, resolveCurrentAccount } from '../utils/msalLogout';
import { getModalPortalRoot } from '../utils/modalPortal';
import './ProfileDropdown.css';

function getViewportWidth() {
  return window.visualViewport?.width ?? window.innerWidth;
}

function getMenuPositionFromTrigger(triggerEl) {
  const rect = triggerEl.getBoundingClientRect();
  return {
    top: Math.round(rect.bottom + 8),
    right: Math.round(Math.max(8, getViewportWidth() - rect.right)),
  };
}

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function LogoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M6 2H3.5A1.5 1.5 0 0 0 2 3.5v9A1.5 1.5 0 0 0 3.5 14H6M10.5 11.5L14 8M14 8L10.5 4.5M14 8H6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ProfileAvatar({ profileImage, displayName, variant }) {
  if (profileImage) {
    return (
      <img
        src={profileImage}
        alt=""
        className="profile-dropdown-avatar"
      />
    );
  }

  return (
    <div
      className={`profile-dropdown-avatar profile-dropdown-avatar--initials${
        variant === 'mobile' ? ' profile-dropdown-avatar--mobile' : ''
      }`}
      aria-hidden="true"
    >
      {getInitials(displayName)}
    </div>
  );
}

function ProfileDropdown({
  displayName = '',
  jobTitle = '',
  email = '',
  profileImage = null,
  variant = 'desktop',
}) {
  const { instance, accounts } = useMsal();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setMenuPosition(null);
  }, []);

  const toggleMenu = useCallback(() => {
    setIsOpen((open) => {
      const next = !open;
      if (next && variant === 'mobile' && triggerRef.current) {
        setMenuPosition(getMenuPositionFromTrigger(triggerRef.current));
      }
      if (!next) {
        setMenuPosition(null);
      }
      return next;
    });
  }, [variant]);

  const syncMenuPosition = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }
    setMenuPosition(getMenuPositionFromTrigger(triggerRef.current));
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    if (variant === 'mobile') {
      syncMenuPosition();
    }

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        const menuEl = document.getElementById('nettles-profile-dropdown-menu');
        if (menuEl && menuEl.contains(event.target)) {
          return;
        }
        closeMenu();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', closeMenu);
    window.addEventListener('orientationchange', closeMenu);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('orientationchange', closeMenu);
    };
  }, [isOpen, closeMenu, variant, syncMenuPosition]);

  const isMobile = variant === 'mobile';
  const account = resolveCurrentAccount(instance, accounts);
  const resolvedEmail =
    email || account?.username || account?.idTokenClaims?.preferred_username || '';

  const handleLogout = async () => {
    if (!account || isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);
    closeMenu();

    try {
      await logoutUser(instance, account);
    } catch (error) {
      console.error('Logout failed:', error);
      setIsLoggingOut(false);
    }
  };

  const menu = isOpen ? (
    <div
      id="nettles-profile-dropdown-menu"
      className={`profile-dropdown-menu${isMobile ? ' profile-dropdown-menu--portal' : ''}`}
      role="menu"
      style={
        isMobile && menuPosition
          ? {
              position: 'fixed',
              top: menuPosition.top,
              right: menuPosition.right,
              left: 'auto',
              zIndex: 10200,
            }
          : undefined
      }
    >
      <div className="profile-dropdown-menu-header">
        <div className="profile-dropdown-menu-name">{displayName}</div>
        {resolvedEmail && (
          <div className="profile-dropdown-menu-email">{resolvedEmail}</div>
        )}
      </div>
      <button
        type="button"
        className="profile-dropdown-logout"
        role="menuitem"
        onClick={handleLogout}
        disabled={isLoggingOut}
      >
        <LogoutIcon />
        {isLoggingOut ? 'Signing out...' : 'Logout'}
      </button>
    </div>
  ) : null;

  return (
    <div className="profile-dropdown" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`profile-dropdown-trigger${
          isMobile ? ' profile-dropdown-trigger--mobile' : ''
        }`}
        onClick={toggleMenu}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="User account menu"
      >
        <ProfileAvatar
          profileImage={profileImage}
          displayName={displayName}
          variant={variant}
        />
        {!isMobile && (
          <div className="profile-dropdown-user-info">
            <span className="profile-dropdown-name">{displayName}</span>
            <span className="profile-dropdown-role">
              {displayName ? jobTitle || 'Manager' : ''}
            </span>
          </div>
        )}
      </button>

      {isMobile && menu
        ? createPortal(menu, getModalPortalRoot() || document.body)
        : menu}
    </div>
  );
}

export default ProfileDropdown;
